import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PiggyBank, ShieldCheck, Undo2 } from "lucide-react";
import api, { fmtEur } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ExportButton } from "../components/ExportButton";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const TIPO_LBL = { tfr: "TFR", f24_inail: "F24 (INAIL)" };

export default function TfrF24() {
  const { selectedStructure, can } = useAuth();
  const qc = useQueryClient();
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [pay, setPay] = useState(null);
  const [amount, setAmount] = useState("");
  const [dataPag, setDataPag] = useState(new Date().toISOString().slice(0, 10));
  const readOnly = !can("buste_paga");

  const { data } = useQuery({
    queryKey: ["tfr_f24", year, selectedStructure],
    queryFn: async () => (await api.get("/tfr_f24/summary", { params: { anno: year, structure_id: selectedStructure } })).data,
  });
  const operatori = data?.operatori || [];
  const tot = data?.totali || {};
  const pagamenti = data?.pagamenti || [];
  const exportCols = [
    { header: "Operatore", accessor: (o) => o.operatore_nome },
    { header: "Struttura", accessor: (o) => o.structure_nome || "" },
    { header: "TFR arretrato", accessor: (o) => fmtEur(o.tfr_arretrato) },
    { header: "TFR maturato", accessor: (o) => fmtEur(o.tfr_maturato) },
    { header: "TFR pagato", accessor: (o) => fmtEur(o.tfr_pagato) },
    { header: "TFR residuo", accessor: (o) => fmtEur(o.tfr_residuo) },
    { header: "F24 INAIL arretrato", accessor: (o) => fmtEur(o.f24_arretrato) },
    { header: "F24 INAIL maturato", accessor: (o) => fmtEur(o.f24_maturato) },
    { header: "F24 INAIL pagato", accessor: (o) => fmtEur(o.f24_pagato) },
    { header: "F24 INAIL residuo", accessor: (o) => fmtEur(o.f24_residuo) },
  ];

  const saveArretrato = async (op, tipo, val) => {
    try {
      await api.post("/tfr_f24/arretrato", { operatore_id: op.operatore_id, tipo, structure_id: op.structure_id, importo: num(val) });
      qc.invalidateQueries({ queryKey: ["tfr_f24"] });
    } catch { toast.error("Errore salvataggio arretrato"); }
  };

  const openPay = (op, tipo) => {
    const max = tipo === "tfr" ? op.tfr_residuo : op.f24_residuo;
    setPay({ op, tipo }); setAmount((max || 0).toFixed(2)); setDataPag(new Date().toISOString().slice(0, 10));
  };
  const confirmPay = async () => {
    try {
      await api.post("/tfr_f24/paga", {
        operatore_nome: pay.op.operatore_nome, operatore_id: pay.op.operatore_id, structure_id: pay.op.structure_id,
        tipo: pay.tipo, importo: num(amount), anno: parseInt(year, 10), data: dataPag,
      });
      toast.success("Pagamento registrato"); setPay(null);
      qc.invalidateQueries({ queryKey: ["tfr_f24"] });
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const undoPay = async (id) => {
    if (!window.confirm("Annullare questo pagamento? L'importo tornerà nei conteggi.")) return;
    try { await api.delete(`/tfr_f24/pagamenti/${id}`); toast.success("Pagamento annullato"); qc.invalidateQueries({ queryKey: ["tfr_f24"] }); }
    catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-head font-bold">TFR e F24</h1>
          <p className="text-sm text-muted-foreground">Accantonamenti annuali per dipendente: TFR e F24 personale (INAIL). Gli F24 mensili sono nella categoria dedicata.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-muted-foreground">Anno:</span>
          <select value={year} onChange={(e) => setYear(e.target.value)} className="h-9 rounded-md border px-2 text-sm font-semibold" data-testid="tfr-year">
            {[0, 1, 2, 3].map((o) => { const y = String(new Date().getFullYear() - o); return <option key={y} value={y}>{y}</option>; })}
          </select>
          <ExportButton filename={`tfr-f24-${year}`} title={`Prospetto TFR e F24 personale ${year}`} subtitle={selectedStructure === "all" ? "Tutte le strutture" : ""} columns={exportCols} rows={operatori} color="#c2660c" testid="export-tfr" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#c2660c" }} data-testid="tfr-tot-residuo">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><PiggyBank size={15} /> TFR da accantonare</div>
          <div className="text-3xl font-head font-bold text-amber-700 mt-1">{fmtEur(tot.tfr_residuo)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Arretrato {fmtEur(tot.tfr_arretrato)} + maturato {fmtEur(tot.tfr_maturato)} − pagato {fmtEur(tot.tfr_pagato)}</div>
        </div>
        <div className="gr-card p-5 border-l-4" style={{ borderColor: "#7c3aed" }} data-testid="f24-tot-residuo">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted-foreground"><ShieldCheck size={15} /> F24 personale (INAIL)</div>
          <div className="text-3xl font-head font-bold text-violet-700 mt-1">{fmtEur(tot.f24_residuo)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">Arretrato {fmtEur(tot.f24_arretrato)} + maturato {fmtEur(tot.f24_maturato)} − pagato {fmtEur(tot.f24_pagato)}</div>
        </div>
      </div>

      <div className="gr-card p-5 overflow-x-auto">
        {operatori.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Nessun dato per {year}. Compila TFR/INAIL nelle Buste Paga.</div>
        ) : (
          <table className="w-full text-sm border-collapse whitespace-nowrap">
            <thead>
              <tr className="text-xs uppercase tracking-wide">
                <th></th>
                <th className="py-1 px-2 text-center text-amber-700 font-bold border-b-2 border-amber-400" colSpan={5}>TFR</th>
                <th className="py-1 px-2 text-center text-violet-700 font-bold border-b-2 border-violet-400" colSpan={5}>F24 (INAIL)</th>
              </tr>
              <tr className="text-left text-muted-foreground border-b">
                <th className="py-2 px-2">Operatore</th>
                <th className="py-2 px-2 text-right">Arretrati</th><th className="py-2 px-2 text-right">Maturato</th><th className="py-2 px-2 text-right">Pagato</th><th className="py-2 px-2 text-right">Residuo</th><th className="py-2 px-2"></th>
                <th className="py-2 px-2 text-right">Arretrati</th><th className="py-2 px-2 text-right">Maturato</th><th className="py-2 px-2 text-right">Pagato</th><th className="py-2 px-2 text-right">Residuo</th><th className="py-2 px-2"></th>
              </tr>
            </thead>
            <tbody>
              {operatori.map((op, i) => (
                <tr key={i} className="border-b last:border-0" data-testid={`tfr-row-${i}`}>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-grid place-items-center h-6 w-6 shrink-0 rounded-full bg-primary/10 text-primary text-xs font-bold tabnum" data-testid={`tfr-num-${i}`}>{i + 1}</span>
                      <div>
                        <div className="font-semibold">{op.operatore_nome}</div>
                        <div className="text-xs text-muted-foreground">{op.structure_nome}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <input type="number" step="0.01" disabled={readOnly} defaultValue={op.tfr_arretrato ?? 0} key={`tfr-arr-${op.operatore_id}-${op.tfr_arretrato}`}
                      onBlur={(e) => { if (num(e.target.value) !== num(op.tfr_arretrato)) saveArretrato(op, "tfr", e.target.value); }}
                      className="w-20 rounded border px-2 py-1 text-sm text-right disabled:opacity-60" data-testid={`tfr-arretrato-${i}`} />
                  </td>
                  <td className="py-2 px-2 text-right">{fmtEur(op.tfr_maturato)}</td>
                  <td className="py-2 px-2 text-right text-emerald-700">{fmtEur(op.tfr_pagato)}</td>
                  <td className="py-2 px-2 text-right font-bold text-amber-700">{fmtEur(op.tfr_residuo)}</td>
                  <td className="py-2 px-2">
                    <Button size="sm" variant="outline" disabled={readOnly || op.tfr_residuo <= 0} onClick={() => openPay(op, "tfr")} data-testid={`pay-tfr-${i}`}>Paga TFR</Button>
                  </td>
                  <td className="py-2 px-2 text-right">
                    <input type="number" step="0.01" disabled={readOnly} defaultValue={op.f24_arretrato ?? 0} key={`f24-arr-${op.operatore_id}-${op.f24_arretrato}`}
                      onBlur={(e) => { if (num(e.target.value) !== num(op.f24_arretrato)) saveArretrato(op, "f24_inail", e.target.value); }}
                      className="w-20 rounded border px-2 py-1 text-sm text-right disabled:opacity-60" data-testid={`f24-arretrato-${i}`} />
                  </td>
                  <td className="py-2 px-2 text-right">{fmtEur(op.f24_maturato)}</td>
                  <td className="py-2 px-2 text-right text-emerald-700">{fmtEur(op.f24_pagato)}</td>
                  <td className="py-2 px-2 text-right font-bold text-violet-700">{fmtEur(op.f24_residuo)}</td>
                  <td className="py-2 px-2">
                    <Button size="sm" variant="outline" disabled={readOnly || op.f24_residuo <= 0} onClick={() => openPay(op, "f24_inail")} data-testid={`pay-f24-${i}`}>Paga F24</Button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 font-bold">
                <td className="py-2 pr-3">Totali residui</td>
                <td className="py-2 px-2 text-right">{fmtEur(tot.tfr_arretrato)}</td>
                <td className="py-2 px-2 text-right">{fmtEur(tot.tfr_maturato)}</td>
                <td className="py-2 px-2 text-right text-emerald-700">{fmtEur(tot.tfr_pagato)}</td>
                <td className="py-2 px-2 text-right text-amber-700">{fmtEur(tot.tfr_residuo)}</td>
                <td></td>
                <td className="py-2 px-2 text-right">{fmtEur(tot.f24_arretrato)}</td>
                <td className="py-2 px-2 text-right">{fmtEur(tot.f24_maturato)}</td>
                <td className="py-2 px-2 text-right text-emerald-700">{fmtEur(tot.f24_pagato)}</td>
                <td className="py-2 px-2 text-right text-violet-700">{fmtEur(tot.f24_residuo)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      {pagamenti.length > 0 && (
        <div className="gr-card p-5">
          <h3 className="font-head font-bold mb-3">Pagamenti registrati ({year})</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b">
                  <th className="py-2">Data</th><th className="py-2">Operatore</th><th className="py-2">Tipo</th><th className="py-2 text-right">Importo</th><th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pagamenti.map((p) => (
                  <tr key={p.id} className="border-b last:border-0" data-testid={`pagamento-${p.id}`}>
                    <td className="py-2">{p.data}</td>
                    <td className="py-2 font-semibold">{p.operatore_nome}</td>
                    <td className="py-2">{TIPO_LBL[p.tipo] || p.tipo}</td>
                    <td className="py-2 text-right font-semibold text-emerald-700">{fmtEur(p.importo)}</td>
                    <td className="py-2 text-right">
                      {!readOnly && <Button size="sm" variant="ghost" onClick={() => undoPay(p.id)} data-testid={`undo-${p.id}`}><Undo2 size={15} className="mr-1" /> Annulla</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Registra pagamento {pay ? TIPO_LBL[pay.tipo] : ""}</DialogTitle></DialogHeader>
          {pay && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{pay.op.operatore_nome} — {pay.op.structure_nome}</div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Importo (€)</Label>
                <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" />
                <p className="text-[11px] text-muted-foreground mt-1">Residuo: {fmtEur(pay.tipo === "tfr" ? pay.op.tfr_residuo : pay.op.f24_residuo)}</p>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Data pagamento</Label>
                <Input type="date" value={dataPag} onChange={(e) => setDataPag(e.target.value)} data-testid="pay-date" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPay(null)}>Annulla</Button>
            <Button onClick={confirmPay} data-testid="pay-confirm" className="font-semibold">Segna come pagato</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
